import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { MagnifyingGlassIcon, TrashIcon, EyeIcon, XCircleIcon } from '@heroicons/react/24/outline';

interface Post {
  _id: string;
  userId: {
    _id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  caption: string;
  image?: string;
  video?: string;
  mediaType?: 'image' | 'video';
  likes: string[];
  reactions: Array<{
    userId: string;
    reactionType: string;
    createdAt: Date;
  }>;
  comments: Array<{
    _id: string;
    userId: string;
    comment: string;
    createdAt: Date;
  }>;
  moderationStatus: 'pending' | 'approved' | 'rejected';
  isModerated: boolean;
  isArchived: boolean;
  isReported: boolean;
  reports: Array<{
    userId: string;
    reason?: string;
    reportedAt: Date;
  }>;
  createdAt: string;
  updatedAt: string;
}

export default function PostsTable() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'reported'>('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, _setLimit] = useState(20);
  const [processing, setProcessing] = useState(false);

  // Modal state
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch, statusFilter]);

  useEffect(() => {
    loadPosts();
  }, [currentPage, limit, activeSearch, statusFilter]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const params: any = { 
        page: currentPage,
        limit: limit,
        includeArchived: true, // Admin can see archived posts
      };
      
      if (activeSearch) params.search = activeSearch;
      if (statusFilter === 'pending') params.moderationStatus = 'pending';
      if (statusFilter === 'approved') params.moderationStatus = 'approved';
      if (statusFilter === 'rejected') params.moderationStatus = 'rejected';
      if (statusFilter === 'reported') params.reportedOnly = true;
      
      const response = await api.get('/admin/posts', { params });
      setPosts(response.data.posts || []);
      setTotalPages(response.data.totalPages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading posts:', error);
      const errorMsg = document.createElement('div');
      errorMsg.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      errorMsg.textContent = 'Failed to load posts';
      document.body.appendChild(errorMsg);
      setTimeout(() => {
        document.body.removeChild(errorMsg);
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setActiveSearch(search);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearFilters = () => {
    setSearch('');
    setActiveSearch('');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  const handleDelete = async (postId: string) => {
    if (!confirm(t('posts.confirmDelete'))) {
      return;
    }

    try {
      setProcessing(true);
      await api.delete(`/community/posts/${postId}`);
      loadPosts();
      const successMsg = document.createElement('div');
      successMsg.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successMsg.textContent = 'Post deleted successfully!';
      document.body.appendChild(successMsg);
      setTimeout(() => {
        document.body.removeChild(successMsg);
      }, 3000);
    } catch (error: any) {
      console.error('Error deleting post:', error);
      const errorMsg = document.createElement('div');
      errorMsg.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      errorMsg.textContent = error.response?.data?.message || 'Failed to delete post';
      document.body.appendChild(errorMsg);
      setTimeout(() => {
        document.body.removeChild(errorMsg);
      }, 3000);
    } finally {
      setProcessing(false);
    }
  };

  const handleModeration = async (postId: string, status: 'approved' | 'rejected') => {
    try {
      setProcessing(true);
      await api.put(`/admin/posts/${postId}/moderate`, { moderationStatus: status });
      loadPosts();
      const successMsg = document.createElement('div');
      successMsg.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      successMsg.textContent = `Post ${status} successfully!`;
      document.body.appendChild(successMsg);
      setTimeout(() => {
        document.body.removeChild(successMsg);
      }, 3000);
    } catch (error: any) {
      console.error(`Error ${status} post:`, error);
      const errorMsg = document.createElement('div');
      errorMsg.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      errorMsg.textContent = error.response?.data?.message || `Failed to ${status} post`;
      document.body.appendChild(errorMsg);
      setTimeout(() => {
        document.body.removeChild(errorMsg);
      }, 3000);
    } finally {
      setProcessing(false);
    }
  };

  const openModal = (post: Post) => {
    setSelectedPost(post);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPost(null);
  };

  const getStatusBadge = (status: string, isReported: boolean, isArchived: boolean) => {
    if (isArchived) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          {t('posts.archived')}
        </span>
      );
    }
    if (isReported) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          {t('posts.reported')}
        </span>
      );
    }
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            {t('posts.approved')}
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            {t('posts.rejected')}
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            {t('posts.pending')}
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-orange"></div>
        <p className="mt-4 text-gray-600">{t('posts.loading')}</p>
      </div>
    );
  }

  const thClass = (alignRight?: boolean) => `px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : alignRight ? 'text-right' : 'text-left'}`;

  return (
    <div className={`bg-white rounded-lg shadow ${isRtl ? 'text-right' : ''}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="p-6 border-b border-gray-200">
        <div className={`flex justify-between items-center mb-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('posts.title')}</h3>
            <p className="text-sm text-gray-500">{t('posts.manageDescription')}</p>
          </div>
        </div>
        <div className="flex flex-col space-y-4">
          <div className={`flex flex-col md:flex-row gap-4 ${isRtl ? 'md:flex-row-reverse' : ''}`}>
            <div className="flex-1 relative flex items-center">
              <MagnifyingGlassIcon className={`absolute w-5 h-5 text-gray-400 ${isRtl ? 'right-3 left-auto' : 'left-3'}`} />
              <input
                type="text"
                placeholder={t('posts.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={handleKeyPress}
                className={`w-full py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent ${isRtl ? 'pr-10 pl-20' : 'pl-10 pr-20'}`}
              />
              <button
                onClick={handleSearch}
                className={`absolute px-4 py-1.5 bg-primary-orange hover:bg-orange-600 text-white rounded-lg text-sm transition-colors ${isRtl ? 'left-2 right-auto' : 'right-2'}`}
              >
                {t('common.search')}
              </button>
            </div>
            <div className={`flex gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-orange focus:border-transparent"
              >
                <option value="all">{t('posts.allStatus')}</option>
                <option value="pending">{t('posts.pending')}</option>
                <option value="approved">{t('posts.approved')}</option>
                <option value="rejected">{t('posts.rejected')}</option>
                <option value="reported">{t('posts.reported')}</option>
              </select>
              {(activeSearch || statusFilter !== 'all') && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {t('common.clearFilters')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={thClass()}>{t('chefs.user')}</th>
              <th className={thClass()}>{t('posts.caption')}</th>
              <th className={thClass()}>Media</th>
              <th className={thClass()}>Engagement</th>
              <th className={thClass()}>Status</th>
              <th className={thClass()}>{t('posts.date')}</th>
              <th className={thClass(true)}>{t('posts.actions')}</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {posts.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  {t('posts.noPosts')}
                </td>
              </tr>
            ) : (
              posts.map((post) => (
                <tr key={post._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {post.userId.avatar ? (
                          <img
                            className="h-10 w-10 rounded-full"
                            src={post.userId.avatar}
                            alt={post.userId.name}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-primary-orange flex items-center justify-center text-white font-semibold">
                            {post.userId.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{post.userId.name}</div>
                        <div className="text-sm text-gray-500">{post.userId.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">
                      {post.caption}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {post.mediaType === 'image' && post.image && (
                      <img src={post.image} alt="Post" className="h-16 w-16 object-cover rounded" />
                    )}
                    {post.mediaType === 'video' && post.video && (
                      <div className="h-16 w-16 bg-gray-200 rounded flex items-center justify-center">
                        <span className="text-xs text-gray-500">Video</span>
                      </div>
                    )}
                    {!post.mediaType && (
                      <span className="text-xs text-gray-400">No media</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <div>❤️ {post.reactions.length}</div>
                      <div>💬 {post.comments.length}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(post.moderationStatus, post.isReported, post.isArchived)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {new Date(post.createdAt).toLocaleDateString()}
                    </div>
                    <div className="text-sm text-gray-500">
                      {new Date(post.createdAt).toLocaleTimeString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className={`flex flex-wrap items-center gap-3 ${isRtl ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
                      <button
                        onClick={() => openModal(post)}
                        className="text-primary-orange hover:text-orange-600"
                      >
                        <EyeIcon className="w-5 h-5" />
                      </button>
                      {post.moderationStatus === 'pending' && (
                        <>
                          <button
                            onClick={() => handleModeration(post._id, 'approved')}
                            className="text-green-600 hover:text-green-700"
                            disabled={processing}
                          >
                            {t('posts.approve')}
                          </button>
                          <button
                            onClick={() => handleModeration(post._id, 'rejected')}
                            className="text-red-600 hover:text-red-700"
                            disabled={processing}
                          >
                            {t('posts.reject')}
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(post._id)}
                        className="text-red-600 hover:text-red-700"
                        disabled={processing}
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
        <div className="flex items-center">
          <span className="text-sm text-gray-700">
            Showing {posts.length > 0 ? (currentPage - 1) * limit + 1 : 0} to{' '}
            {Math.min(currentPage * limit, total)} of {total} posts
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      {/* Post Details Modal */}
      {showModal && selectedPost && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Post Details</h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* User Info */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Author</h4>
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    {selectedPost.userId.avatar ? (
                      <img
                        className="h-12 w-12 rounded-full"
                        src={selectedPost.userId.avatar}
                        alt={selectedPost.userId.name}
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-primary-orange flex items-center justify-center text-white font-semibold">
                        {selectedPost.userId.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <div className="text-sm font-medium text-gray-900">{selectedPost.userId.name}</div>
                      <div className="text-sm text-gray-500">{selectedPost.userId.email}</div>
                    </div>
                  </div>
                </div>

                {/* Caption */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Caption</h4>
                  <p className="text-sm text-gray-900 p-3 bg-gray-50 rounded-lg">
                    {selectedPost.caption}
                  </p>
                </div>

                {/* Media */}
                {selectedPost.mediaType && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      {selectedPost.mediaType === 'image' ? 'Image' : 'Video'}
                    </h4>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      {selectedPost.mediaType === 'image' && selectedPost.image && (
                        <img src={selectedPost.image} alt="Post" className="max-w-full h-auto rounded" />
                      )}
                      {selectedPost.mediaType === 'video' && selectedPost.video && (
                        <video src={selectedPost.video} controls className="max-w-full rounded" />
                      )}
                    </div>
                  </div>
                )}

                {/* Engagement Stats */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Engagement</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-gray-900">{selectedPost.reactions.length}</div>
                      <div className="text-sm text-gray-500">Reactions</div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-gray-900">{selectedPost.comments.length}</div>
                      <div className="text-sm text-gray-500">Comments</div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-gray-900">{selectedPost.likes.length}</div>
                      <div className="text-sm text-gray-500">Likes</div>
                    </div>
                  </div>
                </div>

                {/* Reports */}
                {selectedPost.reports && selectedPost.reports.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Reports ({selectedPost.reports.length})</h4>
                    <div className="space-y-2">
                      {selectedPost.reports.map((report, idx) => (
                        <div key={idx} className="p-3 bg-red-50 rounded-lg">
                          <div className="text-sm text-gray-900">
                            {report.reason || 'No reason provided'}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Reported on {new Date(report.reportedAt).toLocaleString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                {selectedPost.moderationStatus === 'pending' && (
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        handleModeration(selectedPost._id, 'approved');
                        closeModal();
                      }}
                      disabled={processing}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        handleModeration(selectedPost._id, 'rejected');
                        closeModal();
                      }}
                      disabled={processing}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
